import { Mail, Phone, Plus, Trash2 } from 'lucide-react';
import type { GuestContactInput, GuestContactType } from '@peles/shared';
import { PhoneInput } from '../common/PhoneInput';

export interface GuestContactsSectionProps {
  /** Current extra contacts (replace-all list held by the guest form). */
  contacts: GuestContactInput[];
  onChange: (contacts: GuestContactInput[]) => void;
}

const TYPE_META: Record<GuestContactType, { icon: typeof Mail; label: string; input: string }> = {
  EMAIL: { icon: Mail, label: 'email', input: 'email' },
  PHONE: { icon: Phone, label: 'phone', input: 'tel' },
};

const valueClass = 'flex-1 min-w-40 px-3 py-1.5 border border-gray-300 rounded-md text-sm';

/**
 * Extra emails and phones for a guest, beyond the primary pair on the form.
 *
 * A household is usually more than one inbox — a couple, a parent who handles
 * the replies — and a message that reaches only the address we happened to
 * import is a message half-delivered. Everything listed here receives every
 * send for this guest.
 */
export function GuestContactsSection({ contacts, onChange }: GuestContactsSectionProps) {
  const add = (contactType: GuestContactType) => {
    onChange([...contacts, { contactType, value: '', label: '' }]);
  };

  const update = (index: number, updates: Partial<GuestContactInput>) => {
    onChange(contacts.map((contact, i) => (i === index ? { ...contact, ...updates } : contact)));
  };

  const remove = (index: number) => {
    onChange(contacts.filter((_, i) => i !== index));
  };

  return (
    <div>
      <span className="block text-sm font-medium text-gray-700 mb-1">
        Additional emails &amp; phones
      </span>
      <p className="text-xs text-gray-500 mb-2">
        Every email here also receives invitations and updates; every phone is included when sending
        SMS.
      </p>

      <div className="space-y-2">
        {contacts.map((contact, index) => {
          const meta = TYPE_META[contact.contactType];
          const Icon = meta.icon;
          return (
            <div key={index} className="flex flex-wrap items-center gap-2">
              <Icon className="w-4 h-4 text-gray-400 shrink-0" aria-hidden="true" />
              {contact.contactType === 'PHONE' ? (
                <PhoneInput
                  value={contact.value}
                  onChange={(value) => update(index, { value })}
                  placeholder="Phone number"
                  aria-label={`Additional ${meta.label} ${index + 1}`}
                  className={valueClass}
                />
              ) : (
                <input
                  type={meta.input}
                  value={contact.value}
                  onChange={(e) => update(index, { value: e.target.value })}
                  placeholder="name@example.com"
                  aria-label={`Additional ${meta.label} ${index + 1}`}
                  className={valueClass}
                />
              )}
              <input
                type="text"
                value={contact.label ?? ''}
                onChange={(e) => update(index, { label: e.target.value })}
                placeholder="Label (optional)"
                aria-label={`Label for additional ${meta.label} ${index + 1}`}
                className="w-32 px-3 py-1.5 border border-gray-300 rounded-md text-sm"
              />
              <button
                type="button"
                onClick={() => remove(index)}
                aria-label={`Remove additional ${meta.label} ${index + 1}`}
                className="p-1.5 text-gray-400 hover:text-red-600"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          );
        })}
      </div>

      <div className="mt-2 flex gap-3">
        <button
          type="button"
          onClick={() => add('EMAIL')}
          className="inline-flex items-center gap-1 text-sm text-primary-700 hover:text-primary-800"
        >
          <Plus className="w-4 h-4" />
          Add email
        </button>
        <button
          type="button"
          onClick={() => add('PHONE')}
          className="inline-flex items-center gap-1 text-sm text-primary-700 hover:text-primary-800"
        >
          <Plus className="w-4 h-4" />
          Add phone
        </button>
      </div>
    </div>
  );
}
